import {AfterViewInit, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges} from '@angular/core';
import {CommonModule, isPlatformBrowser} from '@angular/common';
import {DataViewLazyLoadEvent, DataViewModule} from 'primeng/dataview';
import {TranslateModule} from '@ngx-translate/core';
import {FeedCardComponent} from '../../../features/feed/shared/feed-card/feed-card.component';
import {DirectivesModule} from '../../directives/directives.module';
import {PullToRefreshDirective} from '../../directives/pull-to-refresh.directive';
import {CacheRefreshService} from '../../../core/services/cache-refresh.service';
import {FeedCollectionDataProviderService, FeedPageResult} from '../feed-collection-data-provider.service';
import {FeedApiModel} from '../../../api/models/feed.api.model';

@Component({
  selector: 'app-home-feed-list',
  standalone: true,
  imports: [CommonModule, DataViewModule, TranslateModule, FeedCardComponent, DirectivesModule],
  templateUrl: './home-feed-list.component.html',
  styleUrl: './home-feed-list.component.scss'
})
export class HomeFeedListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() preloadedPage: FeedPageResult | null = null;
  /** Static, non-interactive Home swipe preview backed only by parent data. */
  @Input() renderMode: 'active' | 'swipe-preview' = 'active';
  /** Distant preview panels preserve their layout without starting image loads. */
  @Input() previewMediaEnabled = true;

  public feeds: FeedApiModel[] = [];
  public totalRecords = 0;
  public readonly pageSize = 20;
  /** NgOptimizedImage priority is immutable for an already-created card. */
  public prioritizeInitialCards = false;
  /**
   * Preview intentionally renders only a viewport-sized static slice. The
   * value is recalculated once after its host is in the DOM; it never creates
   * a resize/scroll observer or starts fetching another page.
   */
  public previewItemCount = 4;
  private readonly previewFallbackItemCount = 4;
  private readonly previewEstimatedCardHeight = 320;
  private previewRenderFrameId: number | null = null;

  public get isSwipePreview(): boolean {
    return this.renderMode === 'swipe-preview';
  }

  public get visibleFeeds(): FeedApiModel[] {
    return this.isSwipePreview ? this.feeds.slice(0, this.previewItemCount) : this.feeds;
  }

  constructor(
    private readonly feedDPS: FeedCollectionDataProviderService,
    private readonly cacheRefresh: CacheRefreshService,
    private readonly host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  ngOnInit(): void {
    // A preview that is later promoted keeps its existing image elements. Do
    // not change their priority after creation: NgOptimizedImage rejects it.
    this.prioritizeInitialCards = !this.isSwipePreview;
    if (this.preloadedPage) {
      this.applyPage(this.preloadedPage);
      return;
    }
    if (this.isSwipePreview) {
      return;
    }
    this.loadPage(1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['renderMode']
      && !changes['renderMode'].firstChange
      && changes['renderMode'].previousValue === 'swipe-preview'
      && this.renderMode === 'active'
    ) {
      this.activateFromSwipePreview();
      return;
    }
    if (changes['preloadedPage'] && !changes['preloadedPage'].firstChange && this.preloadedPage) {
      this.applyPage(this.preloadedPage);
    }
  }

  ngAfterViewInit(): void {
    if (!this.isSwipePreview) {
      return;
    }

    // The initial value is SSR-safe. Measure after Angular's verification
    // pass to avoid changing the DataView during ngAfterViewInit.
    this.previewRenderFrameId = requestAnimationFrame(() => {
      this.previewRenderFrameId = null;
      if (this.isSwipePreview) {
        this.previewItemCount = this.resolvePreviewItemCount();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.previewRenderFrameId !== null) {
      cancelAnimationFrame(this.previewRenderFrameId);
      this.previewRenderFrameId = null;
    }
  }

  public loadNextFeedsChunk(event: DataViewLazyLoadEvent): void {
    if (this.isSwipePreview) {
      return;
    }
    const page = Math.floor((event.first || 0) / (event.rows || this.pageSize)) + 1;
    this.loadPage(page);
  }

  public onPullToRefresh(pullToRefresh: PullToRefreshDirective): void {
    if (this.isSwipePreview) {
      pullToRefresh.complete();
      return;
    }
    this.cacheRefresh.requestRefresh();
    this.feedDPS.getPage(1, this.pageSize).subscribe({
      next: (page) => {
        this.applyPage(page);
        pullToRefresh.complete();
      },
      error: () => pullToRefresh.complete()
    });
  }

  private loadPage(page: number): void {
    this.feedDPS.getPage(page, this.pageSize).subscribe((result) => this.applyPage(result));
  }

  /** Enables the normal list behaviour on the existing swipe-preview view. */
  private activateFromSwipePreview(): void {
    if (this.preloadedPage) {
      this.applyPage(this.preloadedPage);
      return;
    }
    this.loadPage(1);
  }

  private applyPage(result: FeedPageResult): void {
    this.feeds = result.records;
    this.totalRecords = result.total;
  }

  private resolvePreviewItemCount(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return this.previewFallbackItemCount;
    }

    const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
    const top = this.host.nativeElement.getBoundingClientRect().top || 0;
    const remainingHeight = Math.max(1, viewportHeight - Math.max(0, top));

    // One card beyond the visible area prevents an exposed blank edge while dragging.
    return Math.max(this.previewFallbackItemCount, Math.ceil(remainingHeight / this.previewEstimatedCardHeight) + 1);
  }
}
